import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { TeeButton } from "@/components/ui/tee-button";
import { TeeConfirmationDialog } from "@/components/ui/tee-confirmation-dialog";
import { useAssistantOptional } from "@/contexts/AssistantContext";
import { useAssistantChat } from "@/hooks/useAssistantChat";
import type {
  AssistantContextTarget,
  AssistantUiMessageDto,
  AssistantScope,
  AssistantSendOptions,
  PendingActionCardDto,
} from "@/types/support-assistant";
import { AssistantComposer } from "./AssistantComposer";
import { AssistantEmptyState } from "./AssistantEmptyState";
import { AssistantFirstRun } from "./AssistantFirstRun";
import { ClarifyChips } from "./ClarifyChips";
import { MessageThread } from "./MessageThread";

export interface AssistantChatProps {
  sessionId: string | null;
  scope?: AssistantScope;
  canSeeTickets?: boolean;
  onViewTicket?: (ticketId: string) => void;
  compact?: boolean;
  /** Rendered below the composer; receives send() so hosts can inject canned messages. */
  renderFooter?: (
    send: (text: string, options?: AssistantSendOptions) => void,
  ) => ReactNode;
  /** Scope attached to every message sent. undefined = full page (no scope sent); null = no context. */
  messageScope?: AssistantContextTarget | null;
  mobile?: boolean;
  showEscalate?: boolean;
  escalatedTicketNumber?: string;
  onSelectSession?: (id: string) => void;
  /** Start a fresh session when the thread is locked by a closed ticket. Falls back to the assistant context. */
  onStartNewChat?: () => void;
}

/** Shared chat body used by the docked panel and the full page (D-16). */
export function AssistantChat({
  sessionId,
  canSeeTickets,
  onViewTicket,
  compact = false,
  renderFooter,
  messageScope,
  mobile = false,
  showEscalate = false,
  escalatedTicketNumber,
  onSelectSession,
  onStartNewChat,
}: AssistantChatProps) {
  const { t } = useTranslation();
  const assistant = useAssistantOptional();
  const {
    messages,
    isStreaming,
    error,
    lastFailure,
    retry: retryFailure,
    sendMessage,
    stop,
    approve,
    reject,
    loadSession,
    resolvingCardIds,
    closedTicket,
    openTicket,
    sendHqMessage,
    closeTicket,
    isSendingHq,
  } = useAssistantChat(sessionId);

  useEffect(() => {
    if (sessionId) loadSession(sessionId).catch(() => undefined);
  }, [sessionId, loadSession]);

  // Read-only view of the panel's polled session; reload the thread when a new
  // HQ reply lands and we are not mid-stream.
  const polled = useQuery<{
    messages: AssistantUiMessageDto[];
    session?: { openTicket?: { id: string; assigneeName?: string } };
  }>({
    queryKey: ["assistant-session", sessionId],
    enabled: false,
  });
  const [confirmClose, setConfirmClose] = useState(false);
  const handoff = !!openTicket && !closedTicket;
  const polledMessages = polled.data?.messages ?? [];
  // Counts HQ replies, requester messages and the open-ticket state so changes
  // made elsewhere (HQ side, another tab) trigger a reload of the thread.
  const hqReplyCount = polledMessages.filter(
    (m) => m.hqReply || m.requesterReply,
  ).length;
  const ot = polled.data?.session?.openTicket;
  const ticketKey = ot ? `${ot.id}|${ot.assigneeName ?? ""}` : "";
  const prevTicketKey = useRef<string | null>(null);
  const prevHqCount = useRef<number | null>(null);
  const pendingHqReload = useRef(false);
  useEffect(() => {
    const prev = prevHqCount.current;
    prevHqCount.current = hqReplyCount;
    if (prev !== null && hqReplyCount > prev) pendingHqReload.current = true;
    const prevKey = prevTicketKey.current;
    prevTicketKey.current = ticketKey;
    if (prevKey !== null && prevKey !== ticketKey) pendingHqReload.current = true;
    if (pendingHqReload.current && !isStreaming && sessionId) {
      pendingHqReload.current = false;
      loadSession(sessionId).catch(() => undefined);
    }
  }, [hqReplyCount, ticketKey, isStreaming, sessionId, loadSession]);

  const firstRunActive = assistant?.firstRunActive ?? false;
  const completeFirstRun = assistant?.completeFirstRun;

  const send = useCallback(
    (text: string, options?: AssistantSendOptions) => {
      if (closedTicket) return;
      if (openTicket) {
        void sendHqMessage(text);
      } else if (messageScope === undefined) {
        if (options) void sendMessage(text, undefined, options);
        else void sendMessage(text);
      } else if (options) void sendMessage(text, messageScope, options);
      else void sendMessage(text, messageScope);
      if (firstRunActive) completeFirstRun?.();
    },
    [
      sendMessage,
      sendHqMessage,
      messageScope,
      closedTicket,
      openTicket,
      firstRunActive,
      completeFirstRun,
    ],
  );

  const escalate = useCallback(
    () => send(t("assistant.escalateRequest"), { intent: "escalate" }),
    [send, t],
  );

  const askAgain = useCallback(
    (_card: PendingActionCardDto) => send(t("assistant.askAgainMessage")),
    [send, t],
  );

  const retry = () => {
    if (lastFailure) void retryFailure();
    else if (sessionId) loadSession(sessionId).catch(() => undefined);
  };

  const wasStreaming = useRef(false);
  const notifyActivity = assistant?.notifyActivity;
  useEffect(() => {
    if (wasStreaming.current && !isStreaming) notifyActivity?.();
    wasStreaming.current = isStreaming;
  }, [isStreaming, notifyActivity]);

  const lastCode = messages[messages.length - 1]?.notice?.code;
  const blocked = lastCode === "kill_switch" || lastCode === "no_access";
  const isEmpty = messages.length === 0;
  const last = messages[messages.length - 1];
  const showYesNo =
    !closedTicket &&
    !handoff &&
    !isStreaming &&
    !blocked &&
    !!sessionId &&
    last?.role === "assistant" &&
    !last.card &&
    !(last.clarify && last.clarify.options.length > 0) &&
    !last.clarify?.searchOptions?.length &&
    !last.notice &&
    !last.stopped &&
    last.text.trim().endsWith("?");

  const scopeKey = messageScope?.type ?? "none";
  const entityLabel =
    messageScope?.type === "module"
      ? messageScope.page
      : messageScope?.type === "location"
        ? messageScope.label
        : ((messageScope?.label ?? "").split(/\s+/)[0] ?? "");
  const gutter = mobile ? "px-4" : "px-[18px]";

  let placeholder: string | undefined;
  if (handoff) {
    placeholder = t("assistant.handoffMode.placeholder");
  } else if (messageScope === null) {
    placeholder = t(
      mobile
        ? "assistant.composer.placeholderShortNone"
        : "assistant.composer.placeholderNone",
    );
  } else if (messageScope) {
    placeholder = t(
      mobile
        ? "assistant.composer.placeholderShort"
        : "assistant.composer.placeholder",
      { entity: entityLabel },
    );
  }

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col ${compact ? "" : "min-h-[480px]"}`}
    >
      {isEmpty && firstRunActive ? (
        <AssistantFirstRun
          context={messageScope ?? null}
          mobile={mobile}
          disabled={blocked || !sessionId}
          onSend={send}
          onEscalate={escalate}
        />
      ) : isEmpty ? (
        <AssistantEmptyState
          context={messageScope ?? null}
          mobile={mobile}
          disabled={blocked || !sessionId}
          onSend={send}
          onSelectSession={onSelectSession}
          onViewAll={assistant?.showHistory}
          onEscalate={escalate}
        />
      ) : (
        <MessageThread
          messages={messages}
          isStreaming={isStreaming}
          resolvingCardIds={resolvingCardIds}
          canSeeTickets={canSeeTickets}
          onApprove={(id) => void approve(id)}
          onReject={(id) => void reject(id)}
          onAskAgain={askAgain}
          onViewTicket={onViewTicket}
          onChipSelect={send}
        />
      )}
      {(error || lastFailure) && (
        <div className={gutter}>
          <div
            role="alert"
            className="mb-2 flex flex-col gap-2 rounded-[8px] border border-[rgba(146,52,48,0.25)] bg-[var(--background-badge-red)] px-[14px] py-[12px]"
          >
            <p className="text-[14px] font-semibold text-[var(--color-maroon)]">
              {t("assistant.error.title", {
                source: t(`assistant.error.sources.${scopeKey}`),
              })}
            </p>
            <p className="text-[13px] text-[var(--text-content-title)]">
              {t("assistant.error.body")}
            </p>
            <div>
              <TeeButton
                variant="secondary"
                size="s"
                onClick={retry}
                text={t("assistant.error.retry")}
              />
            </div>
          </div>
        </div>
      )}
      {showYesNo && (
        <div className={`${gutter} pb-2`}>
          <ClarifyChips
            options={[
              t("assistant.chat.quickYes"),
              t("assistant.chat.quickNo"),
            ]}
            onSelect={send}
          />
        </div>
      )}
      {closedTicket ? (
        <div className={`${gutter} pb-3`}>
          <div
            role="status"
            className="flex flex-col gap-2 rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card-elevated)] px-[14px] py-[12px]"
          >
            <p className="text-[14px] font-semibold text-[var(--text-content-title)]">
              {t("assistant.closed.title")}
            </p>
            <p className="text-[13px] text-[var(--text-content-sub-title)]">
              {closedTicket.ticketNumber
                ? t("assistant.closed.body", {
                    ticketNumber: closedTicket.ticketNumber,
                  })
                : t("assistant.closed.bodyNoTicket")}
            </p>
            <div>
              <TeeButton
                variant="primary"
                size="s"
                text={t("assistant.closed.startNew")}
                onClick={
                  onStartNewChat ??
                  (() => void assistant?.startNewConversation())
                }
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          {handoff && openTicket && (
            <div className={`${gutter} pb-2`}>
              <div
                role="status"
                className="flex flex-col gap-2 rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card-elevated)] px-[14px] py-[12px]"
              >
                <p className="text-[14px] font-semibold text-[var(--text-content-title)]">
                  {t("assistant.handoffMode.title")}
                </p>
                <p className="text-[13px] text-[var(--text-content-sub-title)]">
                  {t("assistant.handoffMode.body", {
                    ticketNumber: openTicket.ticketNumber,
                  })}{" "}
                  {openTicket.assigneeName
                    ? t("assistant.handoffMode.assigned", {
                        name: openTicket.assigneeName,
                      })
                    : t("assistant.handoffMode.queue")}
                </p>
                <div>
                  <TeeButton
                    variant="secondary"
                    size="s"
                    text={t("assistant.handoffMode.closeTicket")}
                    onClick={() => setConfirmClose(true)}
                  />
                </div>
              </div>
            </div>
          )}
          <AssistantComposer
            isStreaming={handoff ? false : isStreaming}
            disabled={blocked || !sessionId || isSendingHq}
            onSend={send}
            onStop={stop}
            placeholder={placeholder}
            mobile={mobile}
            onEscalate={showEscalate && !handoff ? escalate : undefined}
            escalatedLabel={
              escalatedTicketNumber && !handoff
                ? t("assistant.composer.escalated", {
                    id: escalatedTicketNumber,
                  })
                : undefined
            }
          />
        </>
      )}
      <TeeConfirmationDialog
        isOpen={confirmClose}
        title={t("assistant.handoffMode.confirmTitle")}
        description={t("assistant.handoffMode.confirmBody")}
        confirmText={t("assistant.handoffMode.confirm")}
        cancelText={t("assistant.handoffMode.cancel")}
        variant="default"
        onConfirm={async () => {
          setConfirmClose(false);
          await closeTicket();
        }}
        onCancel={() => setConfirmClose(false)}
      />
      {renderFooter?.(send)}
    </div>
  );
}
